import React, { useState } from 'react';
import {
  Drawer, Box, Stack, Typography, IconButton, Divider, Button,
  FormControl, RadioGroup, FormControlLabel, Radio,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GoogleIcon from '@mui/icons-material/Google';
import AppleIcon from '@mui/icons-material/Apple';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { updateStylistDetails } from '@services/stylistService';
import { syncIOSCalendar } from '@services/reservationService';
import { isNativeIOS, syncDeviceCalendar } from '@services/iosCalendarNative';
import IOSCalendarUrlDialog from '../IOSCalendarUrlDialog';
import type { Stylist } from '@app-types/models/stylist';
import type { FormDataValue } from '@services/api';

const policyUrl = (lang: string): string => (lang === 'zh' || lang === 'zh-HK'
  ? 'https://cms.hkstylist.com/%e6%94%b9%e6%9c%9f%e8%88%87%e5%8f%96%e6%b6%88%e6%94%bf%e7%ad%96/'
  : 'https://cms.hkstylist.com/rescheduling-and-cancellations-policy/');

interface SectionProps {
  label?: string;
  children?: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ label, children }) => (
  <Box sx={{ py: 2 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.4, textTransform: 'uppercase', color: 'text.secondary', mb: 1.5 }}>{label}</Typography>
    {children}
  </Box>
);

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  stylist?: Stylist | null;
  onRefresh: () => Promise<void> | void;
  onGoogleConnect: () => void;
}

// Settings drawer for the v2 dashboard: reservation mode, calendar sync, policy.
const SettingsDrawer: React.FC<SettingsDrawerProps> = ({ open, onClose, stylist, onRefresh, onGoogleConnect }) => {
  const { t, i18n } = useTranslation();
  const [iosOpen, setIosOpen] = useState(false);
  const [iosLoading, setIosLoading] = useState(false);
  const [iosError, setIosError] = useState('');
  const [iosSuccess, setIosSuccess] = useState('');
  const [iosSyncing, setIosSyncing] = useState(false);
  const [appleSyncing, setAppleSyncing] = useState(false);
  const native = isNativeIOS();

  // Native Apple Calendar (EventKit) connect/sync — one permission tap, no public calendar, no password.
  const handleAppleConnect = async () => {
    if (!stylist?._id) return;
    setAppleSyncing(true);
    try {
      const res = await syncDeviceCalendar(stylist._id);
      if (res?.granted === false) {
        toast.error(t('reservationsV2.appleCalendarPermissionDenied'));
      } else {
        toast.success(t('reservationsV2.appleCalendarSynced', { count: res?.count ?? 0 }));
        await onRefresh();
      }
    } catch (err) {
      console.error('Apple calendar sync failed:', err);
      toast.error(t('reservationsV2.errorAction'));
    } finally {
      setAppleSyncing(false);
    }
  };

  const handleModeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const reservationMode = e.target.value;
    if (!stylist?._id || reservationMode === stylist.reservationMode) return;
    try {
      await updateStylistDetails({ _id: stylist._id, reservationMode });
      toast.success(t('reservationsPage.reservationModeUpdated'));
      await onRefresh();
    } catch (err) {
      console.error('Update reservation mode failed:', err);
      toast.error(t('reservationsPage.errorUpdatingReservationMode'));
    }
  };

  const handleSaveIosUrl = async (url: string) => {
    setIosLoading(true); setIosError(''); setIosSuccess('');
    try {
      const value = url && url.trim() !== '' ? url.trim() : null;
      await updateStylistDetails({ ...stylist, iosCalendarUrl: value } as unknown as Record<string, FormDataValue>);
      setIosSuccess(t('reservationsPage.iosCalendarDialogSuccess'));
      await onRefresh();
      setTimeout(() => { setIosOpen(false); setIosSuccess(''); }, 1200);
    } catch {
      setIosError(t('reservationsPage.iosCalendarDialogError'));
    } finally {
      setIosLoading(false);
    }
  };

  const handleIosSync = async () => {
    if (!stylist?.iosCalendarUrl) return;
    setIosSyncing(true);
    try {
      await syncIOSCalendar(stylist._id);
      toast.success(t('reservationsPage.iosCalendarSyncInitiated'));
      setTimeout(onRefresh, 3000);
    } catch (err) {
      const axiosErr = err as { response?: { status?: number; data?: { message?: string; remainingSeconds?: number } }; message?: string };
      const msg = axiosErr.response?.data?.message || axiosErr.message;
      if (axiosErr.response?.status === 429) {
        toast.error(t('reservationsPage.rateLimitError', { seconds: axiosErr.response?.data?.remainingSeconds || 'a few' }));
      } else {
        toast.error(`${t('reservationsPage.errorSyncingIOSCalendar')}: ${msg}`);
      }
    } finally {
      setIosSyncing(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '85vw', sm: 380 }, p: 2.5 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight={700}>{t('reservationsV2.settings')}</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </Stack>
      <Typography variant="body2" color="text.secondary">{stylist?.displayName}</Typography>
      <Divider sx={{ mt: 1 }} />

      {/* Reservation mode */}
      <Section label={t('stylistProfileSettings.reservationMode')}>
        <FormControl component="fieldset">
          <RadioGroup value={stylist?.reservationMode || 'auto-confirm'} onChange={handleModeChange}>
            <FormControlLabel value="auto-confirm" control={<Radio />} label={t('stylistProfileSettings.autoConfirm')} />
            <FormControlLabel value="need-approval" control={<Radio />} label={t('stylistProfileSettings.needApproval')} />
          </RadioGroup>
        </FormControl>
      </Section>
      <Divider />

      {/* Calendar sync */}
      <Section label={t('reservationsV2.calendarSync')}>
        {stylist?.lastSyncTime && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {t('reservationsV2.lastSynced')}: {format(new Date(stylist.lastSyncTime), 'HH:mm dd-MM-yyyy')}
          </Typography>
        )}
        <Stack spacing={1.25}>
          <Button variant="outlined" startIcon={<GoogleIcon />} onClick={onGoogleConnect} fullWidth sx={{ justifyContent: 'flex-start' }}>
            {t('reservationsPage.syncWithGoogleCalendar')}
          </Button>

          {native ? (
            /* Native app: read the device's Apple Calendar directly via EventKit. */
            <Button variant="outlined" startIcon={<AppleIcon />} onClick={handleAppleConnect} disabled={appleSyncing} fullWidth sx={{ justifyContent: 'flex-start' }}>
              {appleSyncing ? t('reservationsPage.syncing') : t('reservationsV2.connectAppleCalendar')}
            </Button>
          ) : (
            /* Web fallback: the legacy public .ics feed URL. */
            <>
              <Button variant="outlined" startIcon={<AppleIcon />} onClick={() => { setIosError(''); setIosSuccess(''); setIosOpen(true); }} fullWidth sx={{ justifyContent: 'flex-start' }}>
                {t('reservationsPage.syncWithIOSCalendar')}
              </Button>
              {stylist?.iosCalendarUrl && (
                <Button variant="text" onClick={handleIosSync} disabled={iosSyncing} fullWidth sx={{ justifyContent: 'flex-start' }}>
                  {iosSyncing ? t('reservationsPage.syncing') : t('reservationsPage.manualSyncIOS')}
                </Button>
              )}
              <Typography variant="caption" color="text.secondary">
                {t('reservationsV2.appleCalendarWebHint')}
              </Typography>
            </>
          )}
        </Stack>
      </Section>
      <Divider />

      {/* Policy */}
      <Section label={t('reservationsV2.policyLabel')}>
        <Button variant="text" startIcon={<DescriptionOutlinedIcon />} onClick={() => window.open(policyUrl(i18n.language), '_blank', 'noopener')} sx={{ justifyContent: 'flex-start' }} fullWidth>
          {t('reservationsPage.policyButton')}
        </Button>
      </Section>

      <IOSCalendarUrlDialog
        open={iosOpen}
        initialUrl={stylist?.iosCalendarUrl || ''}
        onClose={() => setIosOpen(false)}
        onSave={handleSaveIosUrl}
        loading={iosLoading}
        error={iosError}
        success={iosSuccess}
      />
    </Drawer>
  );
};

export default SettingsDrawer;
